// Bills.js - Complete Unified Component
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaMoneyBillWave,
  FaPlusCircle,
  FaEye,
  FaEdit,
  FaChevronLeft,
  FaChevronRight,
  FaRegComment,
  FaCheckCircle,
  FaClock,
  FaFileInvoice,
  FaTimes,
  FaSave,
  FaCalendarAlt,
  FaSearch
} from "react-icons/fa";
import { FaReceipt } from "react-icons/fa";
import { RiDeleteBinFill } from "react-icons/ri";
import { MdOutlineArrowDropDown } from "react-icons/md";
import './Bills.css';

const Bills = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeRegion, setActiveRegion] = useState(() => {
    if (location.pathname.endsWith('/pk')) return 'pk';
    return 'uk';
  });

  useEffect(() => {
    if (location.pathname.endsWith('/pk')) {
      setActiveRegion('pk');
    } else {
      setActiveRegion('uk');
    }
  }, [location.pathname]);

  const [showPayBillsDropdown, setShowPayBillsDropdown] = useState(false);
  const [showNewBillDropdown, setShowNewBillDropdown] = useState(false);
  const [showBatchActionsDropdown, setShowBatchActionsDropdown] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [editBillData, setEditBillData] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBills, setSelectedBills] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [filterDates, setFilterDates] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [amountRange, setAmountRange] = useState('between');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  const [sourceBills, setSourceBills] = useState([]);
  const [billsData, setBillsData] = useState([]);

  useEffect(() => {
    setSourceBills([]);
    setBillsData([]);
    setSelectedBills([]);
    setSelectedStatus('all');
    setSelectedFilter('all');
    setSelectedDateRange('all');
    setSearchTerm('');
  }, [activeRegion]);

  const payBillsRef = useRef(null);
  const newBillRef = useRef(null);
  const batchActionsRef = useRef(null);
  const payBillsBtnRef = useRef(null);
  const newBillBtnRef = useRef(null);
  const batchActionsBtnRef = useRef(null);
  const feedbackBtnRef = useRef(null);
  const filterBtnRef = useRef(null);
  const filterModalRef = useRef(null);
  const dateBtnRef = useRef(null);
  const dateDropdownRef = useRef(null);
  const viewModalRef = useRef(null);
  const editModalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (payBillsBtnRef.current && !payBillsBtnRef.current.contains(event.target) &&
          payBillsRef.current && !payBillsRef.current.contains(event.target)) {
        setShowPayBillsDropdown(false);
      }
      if (newBillBtnRef.current && !newBillBtnRef.current.contains(event.target) &&
          newBillRef.current && !newBillRef.current.contains(event.target)) {
        setShowNewBillDropdown(false);
      }
      if (batchActionsBtnRef.current && !batchActionsBtnRef.current.contains(event.target) &&
          batchActionsRef.current && !batchActionsRef.current.contains(event.target)) {
        setShowBatchActionsDropdown(false);
      }
      if (dateBtnRef.current && !dateBtnRef.current.contains(event.target) &&
          dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
      if (viewModalRef.current && !viewModalRef.current.contains(event.target) && showViewModal) {
        setShowViewModal(false);
      }
      if (editModalRef.current && !editModalRef.current.contains(event.target) && showEditModal) {
        setShowEditModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showViewModal, showEditModal]);

  const billTypes = [
    { name: 'Upload from computer', route: '/bill/upload' },
    { name: 'Create bill', route: '/bill/create' },
    { name: 'Create recurring bill', route: '/recurring-bill/create' }
  ];

  const handleFeedback = () => navigate('/feedback');

  const handlePayBills = () => {
    setShowPayBillsDropdown(!showPayBillsDropdown);
    setShowNewBillDropdown(false);
    setShowBatchActionsDropdown(false);
  };

  const handleNewBill = () => {
    setShowNewBillDropdown(!showNewBillDropdown);
    setShowPayBillsDropdown(false);
    setShowBatchActionsDropdown(false);
  };

  const handleBatchActions = () => {
    setShowBatchActionsDropdown(!showBatchActionsDropdown);
    setShowPayBillsDropdown(false);
    setShowNewBillDropdown(false);
  };

  const handleMakePayment = () => {
    if (selectedBills.length === 0) {
      alert('Please select at least one bill to pay');
      return;
    }
    alert(`Pay ${selectedBills.length} selected bill(s)`);
    setShowPayBillsDropdown(false);
  };

  const handleConfirmSelected = () => {
    if (selectedBills.length === 0) {
      alert('Please select at least one bill to confirm');
      return;
    }
    alert(`Confirmed ${selectedBills.length} selected bill(s)`);
    setShowBatchActionsDropdown(false);
  };

  const handleDeleteSelected = () => {
    if (selectedBills.length === 0) {
      alert('Please select at least one bill to delete');
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${selectedBills.length} selected bill(s)?`)) {
      const updatedBills = billsData.filter(bill => !selectedBills.includes(bill.id));
      setBillsData(updatedBills);
      setSelectedBills([]);
      alert(`Deleted ${selectedBills.length} selected bill(s)`);
    }
    setShowBatchActionsDropdown(false);
  };

  const handleBillType = (type, route) => {
    setShowNewBillDropdown(false);
    if (route) navigate(route);
    else alert(`Create new ${type}`);
  };

  const handleSelectBill = (id) => {
    setSelectedBills(prev => {
      if (prev.includes(id)) return prev.filter(billId => billId !== id);
      else return [...prev, id];
    });
  };

  const handleSelectAll = () => {
    if (selectedBills.length === filteredData.length) setSelectedBills([]);
    else setSelectedBills(filteredData.map(bill => bill.id));
  };

  const handleViewBill = (bill) => {
    setSelectedBill(bill);
    setShowViewModal(true);
  };

  const handleEditBill = (bill) => {
    setEditBillData({ ...bill });
    setShowEditModal(true);
  };

  const handleSaveEdit = () => {
    if (editBillData) {
      const formattedAmount = activeRegion === 'uk'
        ? `£${editBillData.billAmount.toFixed(2)}`
        : `₨ ${editBillData.billAmount.toLocaleString()}`;

      const updatedBills = billsData.map(bill =>
        bill.id === editBillData.id ? { ...editBillData, billAmountFormatted: formattedAmount } : bill
      );
      setBillsData(updatedBills);
      alert(`Bill ${editBillData.billNo} updated successfully!`);
      setShowEditModal(false);
      setEditBillData(null);
    }
  };

  const handleEditInputChange = (field, value) => {
    setEditBillData(prev => ({ ...prev, [field]: value }));
  };

  const handleDeleteBill = (bill) => {
    if (window.confirm(`Are you sure you want to delete bill ${bill.billNo}?`)) {
      const updatedBills = billsData.filter(b => b.id !== bill.id);
      setBillsData(updatedBills);
      alert(`Deleted bill: ${bill.billNo}`);
    }
  };

  const handleApplyFilter = () => {
    setShowFilterModal(false);
    let filtered = [...billsData];
    if (filterCategory !== 'all') {
      filtered = filtered.filter(item => item.category.toLowerCase() === filterCategory.toLowerCase());
    }
    if (minAmount || maxAmount) {
      filtered = filtered.filter(item => {
        const amount = item.billAmount;
        if (amountRange === 'between') {
          return (!minAmount || amount >= parseFloat(minAmount)) && (!maxAmount || amount <= parseFloat(maxAmount));
        } else if (amountRange === 'equal') {
          return amount === parseFloat(minAmount);
        } else if (amountRange === 'greater') {
          return amount >= parseFloat(minAmount);
        } else if (amountRange === 'less') {
          return amount <= parseFloat(minAmount);
        }
        return true;
      });
    }
    setBillsData(filtered);
    alert('Filter applied!');
  };

  const handleResetFilter = () => {
    setFilterDates('all');
    setFilterCategory('all');
    setAmountRange('between');
    setMinAmount('');
    setMaxAmount('');
    setSelectedFilter('all');
    setSelectedDateRange('all');
    setSelectedStatus('all');
    setSearchTerm('');
    setBillsData(sourceBills);
    alert('Filters reset!');
  };

  const getFilteredData = () => {
    let filtered = [...billsData];
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(item => item.category.toLowerCase() === selectedFilter.toLowerCase());
    }
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(item => item.status === selectedStatus);
    }
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(item =>
        [item.supplier, item.billNo, item.bill, item.category].some(v => String(v || '').toLowerCase().includes(term))
      );
    }
    return filtered;
  };

  const filteredData = getFilteredData();
  const unpaidCount = billsData.filter(bill => bill.status === 'unpaid').length;
  const paidCount = billsData.filter(bill => bill.status === 'paid').length;
  const currencySymbol = activeRegion === 'uk' ? '£' : '₨';

  const renderBillsTable = () => (
    <table className="bills-table">
      <thead>
        <tr>
          <th className="checkbox-col">
            <input type="checkbox" checked={selectedBills.length === filteredData.length && filteredData.length > 0} onChange={handleSelectAll} className="select-all-checkbox" />
          </th>
          <th>Bill</th>
          <th>Source</th>
          <th>Supplier</th>
          <th>Bill No</th>
          <th>Bill Date</th>
          <th>Category</th>
          <th>Due Date</th>
          <th>Bill Amount</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {filteredData.length === 0 && (
          <tr>
            <td colSpan="10" style={{ textAlign: 'center', padding: '24px' }}>No bills found</td>
          </tr>
        )}
        {filteredData.map((item) => (
          <tr key={item.id} className={`bill-row ${item.status}`}>
            <td className="checkbox-col">
              <input type="checkbox" checked={selectedBills.includes(item.id)} onChange={() => handleSelectBill(item.id)} className="bill-checkbox" />
            </td>
            <td>
              <div className="bill-type">
                <FaFileInvoice className="bill-icon" />
                {item.bill}
              </div>
            </td>
            <td>{item.source}</td>
            <td>{item.supplier}</td>
            <td>{item.billNo}</td>
            <td>{item.billDate}</td>
            <td>{item.category}</td>
            <td className={new Date(item.dueDate) < new Date() && item.status === 'unpaid' ? 'overdue' : ''}>{item.dueDate}</td>
            <td className="bill-amount">{item.billAmountFormatted}</td>
            <td>
              <div className="action-buttons">
                <button className="icon-action-btn view" onClick={() => handleViewBill(item)} title="View"><FaEye /></button>
                <button className="icon-action-btn edit" onClick={() => handleEditBill(item)} title="Edit"><FaEdit /></button>
                <button className="icon-action-btn delete" onClick={() => handleDeleteBill(item)} title="Delete"><RiDeleteBinFill /></button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="bills-main">
      <div className="bills-content-wrapper">
        <div className="bills-header-section">
          <div className="bills-title-section">
            <h2><FaReceipt className="title-icon" /> Bills</h2>
          </div>
          <div className="bills-action-buttons">
            <div className="button-wrapper">
              <button ref={feedbackBtnRef} className="action-btn feedback-btn" onClick={handleFeedback}><FaRegComment className="btn-icon" />Feedback</button>
            </div>
            <div className="button-wrapper">
              <button ref={payBillsBtnRef} className="action-btn paybills-btn" onClick={handlePayBills}><FaMoneyBillWave className="btn-icon" />Pay bills</button>
              {showPayBillsDropdown && (
                <div className="action-dropdown paybills-dropdown" ref={payBillsRef}>
                  <div className="dropdown-item-action" onClick={handleMakePayment}>
                    <div className="dropdown-item-content"><div className="dropdown-item-title">Make Recurring Payment</div></div>
                  </div>
                </div>
              )}
            </div>
            <div className="button-wrapper">
              <button ref={newBillBtnRef} className="action-btn newbill-btn" onClick={handleNewBill}><FaPlusCircle className="btn-icon" />New Bill</button>
              {showNewBillDropdown && (
                <div className="action-dropdown bill-dropdown" ref={newBillRef}>
                  <div className="bill-list">
                    {billTypes.map((type, index) => (
                      <div key={index} className="bill-list-item" onClick={() => handleBillType(type.name, type.route)}><div className="bill-list-name">{type.name}</div></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bills-filter-bar">
          <div className="bills-status-tabs">
            <button className={`status-tab ${selectedStatus === 'all' ? 'active' : ''}`} onClick={() => setSelectedStatus('all')}>All Bills ({billsData.length})</button>
            <button className={`status-tab unpaid ${selectedStatus === 'unpaid' ? 'active' : ''}`} onClick={() => setSelectedStatus('unpaid')}><FaClock className="status-icon" />Unpaid ({unpaidCount})</button>
            <button className={`status-tab paid ${selectedStatus === 'paid' ? 'active' : ''}`} onClick={() => setSelectedStatus('paid')}><FaCheckCircle className="status-icon" />Paid ({paidCount})</button>
          </div>
          <div className="bills-toolbar">
            <div className="date-filter-wrapper">
              <div className="date-filter-btn" ref={dateBtnRef} onClick={() => setShowDateDropdown(!showDateDropdown)}>
                <FaCalendarAlt className="calendar-icon" />
                <span>Dates: {selectedDateRange === 'all' ? 'All Dates' : selectedDateRange === 'today' ? 'Today' : selectedDateRange === 'week' ? 'Last 7 Days' : 'Last 30 Days'}<MdOutlineArrowDropDown className="dropdown-icon" /></span>
              </div>
              {showDateDropdown && (
                <div className="dropdown-menu" ref={dateDropdownRef}>
                  <div className="dropdown-item" onClick={() => { setSelectedDateRange('all'); setShowDateDropdown(false); }}>All Dates</div>
                  <div className="dropdown-item" onClick={() => { setSelectedDateRange('today'); setShowDateDropdown(false); }}>Today</div>
                  <div className="dropdown-item" onClick={() => { setSelectedDateRange('week'); setShowDateDropdown(false); }}>Last 7 Days</div>
                  <div className="dropdown-item" onClick={() => { setSelectedDateRange('month'); setShowDateDropdown(false); }}>Last 30 Days</div>
                </div>
              )}
            </div>
            <div className="filter-actions-wrapper">
              <button ref={filterBtnRef} className="filter-actions-btn" onClick={() => setShowFilterModal(true)}>Filter <MdOutlineArrowDropDown className="dropdown-icon" /></button>
            </div>
            <div className="bills-search">
              <FaSearch className="search-icon" />
              <input type="text" placeholder="Search bills..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <div className="batch-actions-wrapper">
              <button ref={batchActionsBtnRef} className="batch-actions-btn" onClick={handleBatchActions}>Batch actions <MdOutlineArrowDropDown className="dropdown-icon" /></button>
              {showBatchActionsDropdown && (
                <div className="batch-actions-dropdown" ref={batchActionsRef}>
                  <div className="batch-action-item" onClick={handleConfirmSelected}>Confirm selected</div>
                  <div className="batch-action-item" onClick={handleDeleteSelected}>Delete selected</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filter Modal */}
        {showFilterModal && (
          <div className="filter-modal-overlay">
            <div className="filter-modal" ref={filterModalRef}>
              <div className="filter-modal-header">
                <h3>Filter</h3>
                <button className="close-modal" onClick={() => setShowFilterModal(false)}><FaTimes /></button>
              </div>
              <div className="filter-modal-body">
                <div className="filter-section">
                  <label className="filter-label">ACCOUNT/CATEGORY</label>
                  <select className="filter-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                    <option value="all">Select category</option>
                    <option value="purchase">Purchase</option>
                    <option value="utilities">Utilities</option>
                    <option value="services">Services</option>
                    <option value="import">Import</option>
                  </select>
                </div>

                <div className="filter-section">
                  <label className="filter-label">AMOUNT</label>
                  <div className="amount-row-container">
                    <div className="amount-select-wrapper">
                      <select className="amount-select" value={amountRange} onChange={(e) => setAmountRange(e.target.value)}>
                        <option value="between">Between</option>
                        <option value="equal">Equal to</option>
                        <option value="greater">Greater than</option>
                        <option value="less">Less than</option>
                      </select>
                    </div>
                    <div className="amount-input-wrapper">
                      <input type="number" className="amount-input" placeholder={`Min (${currencySymbol})`} value={minAmount} onChange={(e) => setMinAmount(e.target.value)} />
                    </div>
                    <div className="amount-input-wrapper">
                      <input type="number" className="amount-input" placeholder={`Max (${currencySymbol})`} value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="filter-modal-footer">
                <button className="reset-filter-btn" onClick={handleResetFilter}>Reset</button>
                <button className="apply-filter-btn" onClick={handleApplyFilter}>Apply</button>
              </div>
            </div>
          </div>
        )}

        {/* View Bill Modal */}
        {showViewModal && selectedBill && (
          <div className="modal-overlay">
            <div className="modal-container" ref={viewModalRef}>
              <div className="modal-header">
                <h3>Review Bill</h3>
                <button className="modal-close-btn" onClick={() => setShowViewModal(false)}><FaTimes /></button>
              </div>
              <div className="modal-content">
                <div className="form-section">
                  <h4>Review the details before submitting</h4>
                  <div className="form-grid">
                    <div className="form-group"><label className="form-label">Payee</label><div className="form-value">{selectedBill.payee}</div></div>
                    <div className="form-row">
                      <div className="form-group"><label className="form-label">Bill Date</label><div className="form-value">{selectedBill.billDate}</div></div>
                      <div className="form-group"><label className="form-label">Due Date</label><div className="form-value">{selectedBill.dueDate}</div></div>
                    </div>
                    <div className="form-group"><label className="form-label">Total Amount</label><div className="form-value amount-value">{selectedBill.billAmountFormatted}</div></div>
                    <div className="form-row">
                      <div className="form-group"><label className="form-label">Description</label><div className="form-value">{selectedBill.description}</div></div>
                      <div className="form-group"><label className="form-label">Account/Category</label><div className="form-value">{selectedBill.category}</div></div>
                    </div>
                    <div className="form-group"><label className="form-label">Bill No</label><div className="form-value">{selectedBill.billNo}</div></div>
                    <div className="form-group"><label className="form-label">Memo</label><div className="form-value memo-value">{selectedBill.memo || 'No memo added'}</div></div>
                  </div>
                </div>
                <div className="modal-action-buttons">
                  <button className="action-btn-secondary" onClick={() => setShowViewModal(false)}>Cancel</button>
                  <button className="action-btn-primary" onClick={() => { setShowViewModal(false); handleEditBill(selectedBill); }}><FaEdit /> Edit Bill</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Bill Modal */}
        {showEditModal && editBillData && (
          <div className="modal-overlay">
            <div className="modal-container" ref={editModalRef}>
              <div className="modal-header">
                <h3>Edit Bill</h3>
                <button className="modal-close-btn" onClick={() => setShowEditModal(false)}><FaTimes /></button>
              </div>
              <div className="modal-content">
                <div className="form-section">
                  <h4>Review the details before submitting</h4>
                  <div className="form-grid">
                    <div className="form-group"><label className="form-label">Payee</label><input type="text" className="form-input" value={editBillData.payee} onChange={(e) => handleEditInputChange('payee', e.target.value)} /></div>
                    <div className="form-row">
                      <div className="form-group"><label className="form-label">Bill Date</label><input type="date" className="form-input" value={editBillData.billDate.split('-').reverse().join('-')} onChange={(e) => { const date = e.target.value.split('-').reverse().join('-'); handleEditInputChange('billDate', date); }} /></div>
                      <div className="form-group"><label className="form-label">Due Date</label><input type="date" className="form-input" value={editBillData.dueDate.split('-').reverse().join('-')} onChange={(e) => { const date = e.target.value.split('-').reverse().join('-'); handleEditInputChange('dueDate', date); }} /></div>
                    </div>
                    <div className="form-group"><label className="form-label">Total Amount</label><input type="number" className="form-input" value={editBillData.billAmount} onChange={(e) => handleEditInputChange('billAmount', parseFloat(e.target.value))} step="0.01" /></div>
                    <div className="form-row">
                      <div className="form-group"><label className="form-label">Description</label><input type="text" className="form-input" value={editBillData.description} onChange={(e) => handleEditInputChange('description', e.target.value)} /></div>
                      <div className="form-group"><label className="form-label">Account/Category</label><select className="form-input" value={editBillData.category} onChange={(e) => handleEditInputChange('category', e.target.value)}><option value="Purchase">Purchase</option><option value="Utilities">Utilities</option><option value="Services">Services</option><option value="Import">Import</option></select></div>
                    </div>
                    <div className="form-group"><label className="form-label">Bill No</label><input type="text" className="form-input" value={editBillData.billNo} onChange={(e) => handleEditInputChange('billNo', e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">Memo</label><textarea className="form-textarea" rows="3" value={editBillData.memo} onChange={(e) => handleEditInputChange('memo', e.target.value)}></textarea></div>
                  </div>
                </div>
                <div className="modal-action-buttons">
                  <button className="action-btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                  <button className="action-btn-primary" onClick={handleSaveEdit}><FaSave /> Save</button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bills-table-container">{renderBillsTable()}</div>
        <div className="bills-pagination">
          <button className="pagination-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}><FaChevronLeft /> Previous</button>
          <button className="pagination-btn" onClick={() => setCurrentPage(prev => prev + 1)}>Next <FaChevronRight /></button>
        </div>
      </div>
    </div>
  );
};

export default Bills;